import React from "react";
import Price from "./price";
import { Link } from "react-router-dom";
import { PlayerStore, Store } from "../store";
import useWantlist from "../hooks/useWantlist";
import { useDefaultImage } from "../hooks/useInventory";
// import { LazyLoadImage } from "react-lazy-load-image-component";
import { useTranslation } from "react-i18next";
import { DateTime } from "luxon";
import useNotification from "../hooks/useNotification";
import useResponsive from "../hooks/useResponsive";
import clone from "clone";
import { PlayerButtonDetailed } from "../routes/item";
import Image from "./image";

const Tile = ({ item }) => {
  const config = Store.useState(s => s.config);
  const forSaleListings = item.listings ? item.listings.filter(l => l.stock.quantity > 0) : [];
  const cheapest = forSaleListings.length ? forSaleListings[0] : null;
  const wantlistEnabled = config.eshop.preferences?.wantlist?.enabled;
  const playerEnabled = config.eshop.preferences?.player?.enabled;

  const ReleaseTile = ({ item, config, cheapest, wantlistEnabled }) => {
    const { addToWantlist } = useWantlist();
    const path = item.path;
    const { loadTracklist, currentTrackData, isPlaying, playPauseTrack } = PlayerStore.useState(s => s);

    const buttonIsPlaying = isPlaying && currentTrackData && currentTrackData.release._id === item._id;
    const active = config.eshop.theme.selected.player?.active;

    const tracklistToLoad = {
      tracklist: item.data.tracklist.filter(t => t.uri).map(t => ({ track: t, release: item })) || [],
      playOffset: 0
    };

    return (
      <div className="tile releaseItem">
        <Link to={path} className="artwork">
          <Image image={item.data?.images[0]} />
        </Link>
        <div className="priceAndActions">
          <PriceAndSecondHandSection listing={cheapest} />
          <div className="actions">
            {playerEnabled && active && item.data.tracklist.filter(t => t.uri).length ? (
              <button type="button" onClick={e => (buttonIsPlaying ? playPauseTrack() : loadTracklist(tracklistToLoad))}>
                <i className={`cg-icon-${buttonIsPlaying ? "pause" : "play"}`} />
              </button>
            ) : null}
            {wantlistEnabled ? (
              <button type="button" onClick={e => addToWantlist(item)}>
                <i className={item.wanted ? "cg-icon-wantlist-fill" : "cg-icon-wantlist"} />
              </button>
            ) : null}
          </div>
        </div>
        <ArtistNames artists={item.data.artists} />
        <p className="title">
          <Link to={path}>{item.data.title}</Link>
        </p>
      </div>
    );
  };

  const ProductTile = ({ item, cheapest, wantlistEnabled }) => {
    const { addToWantlist } = useWantlist();

    const path = item.path;

    return (
      <div className="tile productItem">
        <Link to={path}>
          <Image image={item.data?.images[0]} />
        </Link>
        <div className="priceAndActions">
          <PriceAndSecondHandSection listing={cheapest} />
          <div className="actions">
            {wantlistEnabled ? (
              <button type="button" onClick={e => addToWantlist(item)}>
                <i className={item.wanted ? "cg-icon-wantlist-fill" : "cg-icon-wantlist"} />
              </button>
            ) : null}
          </div>
        </div>
        <p className="title">
          <Link to={path}>{item.data.title}</Link>
        </p>
      </div>
    );
  };

  const BookTile = ({ item, cheapest, wantlistEnabled }) => {
    const { addToWantlist } = useWantlist();

    const path = item.path;

    return (
      <div className="tile productItem">
        <Link to={path}>
          <Image image={item.data?.images[0]} />
        </Link>
        <div className="priceAndActions">
          <PriceAndSecondHandSection listing={cheapest} />
          <div className="actions">
            {wantlistEnabled ? (
              <button type="button" onClick={e => addToWantlist(item)}>
                <i className={item.wanted ? "cg-icon-wantlist-fill" : "cg-icon-wantlist"} />
              </button>
            ) : null}
          </div>
        </div>
        <p className="title">
          <Link to={path}>
            {item.data.title} {item.data.subtitle ? <span>-{item.data.subtitle}</span> : null}
          </Link>
        </p>
      </div>
    );
  };

  if (item.type === "ReleaseItem") return <ReleaseTile item={item} config={config} cheapest={cheapest} listings={forSaleListings} wantlistEnabled={wantlistEnabled} />;
  else if (item.type === "ProductItem") return <ProductTile item={item} cheapest={cheapest} listings={forSaleListings} wantlistEnabled={wantlistEnabled} />;
  else if (item.type === "BookItem") return <BookTile item={item} cheapest={cheapest} listings={forSaleListings} wantlistEnabled={wantlistEnabled} />;
};

const List = ({ item }) => {
  const config = Store.useState(s => s.config);
  const forSaleListings = item.listings ? item.listings.filter(l => l.stock.quantity > 0) : [];
  const cheapest = forSaleListings.length ? forSaleListings[0] : null;
  const wantlistEnabled = config.eshop.preferences?.wantlist?.enabled;
  const playerEnabled = config.eshop.preferences?.player?.enabled;
  const { addNotification } = useNotification();
  const { t } = useTranslation();
  const { isMobile } = useResponsive();

  const AddToWantlist = ({ item, t }) => {
    const { addToWantlist } = useWantlist();

    return (
      <button type="button" onClick={e => addToWantlist(item)}>
        <i className={item.wanted ? "cg-icon-wantlist-fill" : "cg-icon-wantlist"} />
      </button>
    );
  };

  const path = item.path;
  const ReleaseList = ({ item }) => {
    const tracklistToLoad = {
      tracklist: item.data.tracklist.filter(t => t.uri).map(t => ({ track: t, release: item })) || [],
      playOffset: 0
    };
    const { loadTracklist, currentTrackData, isPlaying, playPauseTrack } = PlayerStore.useState(s => s);
    const buttonIsPlaying = isPlaying && currentTrackData && currentTrackData.release._id === item._id;
    const active = config.eshop.theme.selected.player?.active;

    return (
      <div className={"list releaseItem" + (isMobile ? " mobile" : "")}>
        <Link to={path} className="artwork">
          <Image image={item.data?.images[0]} />
        </Link>
        <div className="information">
          <ArtistNames artists={item.data.artists} />
          <p className="title">
            <Link to={path}>{item.data.title}</Link>
          </p>
          <Specs item={item} t={t} />
        </div>
        <div className="priceAndActions">
          <PriceAndSecondHandSection listing={cheapest} />
          <div className="actions">
            {playerEnabled && active && item.data.tracklist.filter(t => t.uri).length ? (
              <button type="button" onClick={e => (buttonIsPlaying ? playPauseTrack() : loadTracklist(tracklistToLoad))}>
                <i className={`cg-icon-${buttonIsPlaying ? "pause" : "play"}`} />
              </button>
            ) : null}
            {wantlistEnabled ? <AddToWantlist t={t} item={item} addNotification={addNotification} /> : null}
          </div>
        </div>
      </div>
    );
  };

  const ProductList = ({ item }) => {
    return (
      <div className={"list prouctItem" + (isMobile ? " mobile" : "")}>
        <Link to={path} className="artwork">
          <Image image={item.data?.images[0]} />
        </Link>
        <div className="information">
          <p className="title">
            <Link to={path}>{item.data.title}</Link>
          </p>
          <Specs item={item} t={t} />
        </div>
        <div className="priceAndActions">
          <PriceAndSecondHandSection listing={cheapest} />
          <div className="actions">{wantlistEnabled ? <AddToWantlist t={t} item={item} addNotification={addNotification} /> : null}</div>
        </div>
      </div>
    );
  };

  const BookList = ({ item }) => {
    return (
      <div className={"list bookItem" + (isMobile ? " mobile" : "")}>
        <Link to={path} className="artwork">
          <Image image={item.data?.images[0]} />
        </Link>
        <div className="information">
          <p className="title">
            <Link to={path}>
              {item.data.title} {item.data.subtitle}
            </Link>
          </p>
          <Specs item={item} t={t} />
        </div>
        <div className="priceAndActions">
          <PriceAndSecondHandSection listing={cheapest} />
          <div className="actions">{wantlistEnabled ? <AddToWantlist t={t} item={item} addNotification={addNotification} /> : null}</div>
        </div>
      </div>
    );
  };

  const Specs = ({ item, t }) => {
    const LabelNames = () => {
      return item.data.labels.map((l, index) => (
        <span key={index + l.name}>
          {l.id ? <Link to={`/catalogue?labels=${l.id}`}>{l.name} </Link> : `${l.name}`} ({l.catno}) <br />
        </span>
      ));
    };

    if (item.type === "ReleaseItem")
      return (
        <div className="specs">
          <p className="labels">
            <LabelNames />
          </p>
          {item.data.releaseDate ? <p>{item.data.country ? <Link to={`/catalogue?countries=${item.data.country}`}>{item.data.country}</Link> : null}</p> : null}
          <div className="styles">
            {item.data.styles.map(s => (
              <Link key={s} to={`/catalogue?styles=${s}`}>
                {s}{" "}
              </Link>
            ))}
          </div>
        </div>
      );
    else if (item.type === "ProductItem")
      return (
        <div className="specs">
          <p>
            {item.data.manufacturer} {item.data.cat ? `(${item.data.cat})` : ""}
          </p>
        </div>
      );
    else if (item.type === "BookItem")
      return (
        <div className="specs">
          {item.data.publisher ? <p>{item.data.publisher}</p> : null}
          <p>
            {item.data.pageCount}p, {item.data.language}
          </p>
          {item.data.categories?.length ? <p>{item.data.categories.join(", ")}</p> : null}

          {item.data.publishedDate ? (
            <p>
              {t("publicationDate")}: {DateTime.fromMillis(item.data.publishedDate).toFormat("DD")}
            </p>
          ) : null}
        </div>
      );
    else return null;
  };

  if (item.type === "ReleaseItem") return <ReleaseList item={item} config={config} cheapest={cheapest} listings={forSaleListings} wantlistEnabled={wantlistEnabled} />;
  else if (item.type === "ProductItem") return <ProductList item={item} cheapest={cheapest} listings={forSaleListings} wantlistEnabled={wantlistEnabled} />;
  else if (item.type === "BookItem") return <BookList item={item} cheapest={cheapest} listings={forSaleListings} wantlistEnabled={wantlistEnabled} />;
};

const Full = ({ item }) => {
  const config = Store.useState(s => s.config);
  const forSaleListings = item.listings ? clone(item.listings).sort((a, b) => (a.prices.sale < b.prices.sale ? -1 : 1)) : [];
  const cheapest = forSaleListings.length ? forSaleListings[0] : null;
  const defaultImage = useDefaultImage();
  const wantlistEnabled = config.eshop.preferences?.wantlist?.enabled;
  const { addNotification } = useNotification();
  const { t } = useTranslation();
  const { isMobile } = useResponsive();

  const AddToWantlist = ({ item, t }) => {
    const { addToWantlist } = useWantlist();

    return (
      <button type="button" onClick={e => addToWantlist(item)}>
        <i className={item.wanted ? "cg-icon-wantlist-fill" : "cg-icon-wantlist"} />
      </button>
    );
  };

  const path = item.path;
  const ReleaseFull = ({ item }) => {
    return (
      <div className={"full releaseItem" + (isMobile ? " mobile" : "")}>
        <Link to={path} className="artwork">
          <Image image={item.data?.images[0]} />
        </Link>
        <div className="right">
          <div className="information">
            <ArtistNames artists={item.data.artists} />
            <p className="title">
              <Link to={path}>{item.data.title}</Link>
            </p>
            <div className="actions">
              <PriceAndSecondHandSection listing={cheapest} />
              <div className="right">{wantlistEnabled ? <AddToWantlist t={t} item={item} addNotification={addNotification} /> : null}</div>
            </div>
            <Specs item={item} t={t} />
            <p className="shortDescription" dangerouslySetInnerHTML={{ __html: item.descriptions.shop.short }} />
            <div className="tracklist">
              {item.data.tracklist
                .filter(t => t.type_ === "track")
                .map((t, index) => (
                  <PlayerButtonDetailed key={`${t.position}-${index}`} track={t} release={item} />
                ))}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const ProductFull = ({ item }) => {
    return (
      <div className={"full productItem" + (isMobile ? " mobile" : "")}>
        <Link to={path} className="artwork">
          <Image image={item.data?.images[0]} />
        </Link>
        <div className="information">
          <p className="title">
            <Link to={path}>{item.data.title}</Link>
          </p>
          <Specs item={item} t={t} />
        </div>
        <div className="actions">
          <PriceAndSecondHandSection listing={cheapest} />
          {wantlistEnabled ? <AddToWantlist t={t} item={item} addNotification={addNotification} /> : null}
        </div>
      </div>
    );
  };

  const BookFull = ({ item }) => {
    return (
      <div className={"full bookItem" + (isMobile ? " mobile" : "")}>
        <Link to={path} className="artwork">
          <Image image={item.data?.images[0]} />
        </Link>
        <div className="information">
          <p className="title">
            <Link to={path}>
              {item.data.title} {item.data.subtitle}
            </Link>
          </p>
          <Specs item={item} t={t} />
        </div>
        <div className="actions">
          <PriceAndSecondHandSection listing={cheapest} />
          {wantlistEnabled ? <AddToWantlist t={t} item={item} addNotification={addNotification} /> : null}
        </div>
      </div>
    );
  };

  const Specs = ({ item, t }) => {
    const LabelNames = () => {
      return item.data.labels.map((l, index) => (
        <span key={index + l.name}>
          {l.id ? <Link to={`/catalogue?labels=${l.id}`}>{l.name} </Link> : `${l.name}`} ({l.catno}) <br />
        </span>
      ));
    };

    if (item.type === "ReleaseItem")
      return (
        <div className="specs">
          <p className="labels">
            <LabelNames />
          </p>
          {item.data.releaseDate ? <p>{item.data.country ? <Link to={`/catalogue?countries=${item.data.country}`}>{item.data.country}</Link> : null}</p> : null}
          <div className="styles">
            {item.data.styles.map(s => (
              <Link key={s} to={`/catalogue?styles=${s}`}>
                {s}{" "}
              </Link>
            ))}
          </div>
        </div>
      );
    else if (item.type === "ProductItem")
      return (
        <div className="specs">
          <p>
            {item.data.manufacturer} {item.data.cat ? `(${item.data.cat})` : ""}
          </p>
        </div>
      );
    else if (item.type === "BookItem")
      return (
        <div className="specs">
          <p>
            {t("publisher")}: {item.data.publisher}
          </p>
          <p>
            {t("pages")}: {item.data.pageCount}
          </p>
          <p>
            {t("categories")}: {item.data.categories.join(", ")}
          </p>
          <p>
            {t("language")}: {item.data.language}
          </p>
          {item.data.publishedDate ? (
            <p>
              {t("publicationDate")}: {DateTime.fromMillis(item.data.publishedDate).toFormat("DD")}
            </p>
          ) : null}
        </div>
      );
    else return null;
  };

  if (item.type === "ReleaseItem") return <ReleaseFull item={item} config={config} defaultImage={defaultImage} cheapest={cheapest} listings={forSaleListings} wantlistEnabled={wantlistEnabled} />;
  else if (item.type === "ProductItem") return <ProductFull item={item} defaultImage={defaultImage} cheapest={cheapest} listings={forSaleListings} wantlistEnabled={wantlistEnabled} />;
  else if (item.type === "BookItem") return <BookFull item={item} defaultImage={defaultImage} cheapest={cheapest} listings={forSaleListings} wantlistEnabled={wantlistEnabled} />;
};

const PriceAndSecondHandSection = ({ listing }) => {
  return (
    <p className="price">
      {listing ? <Price listing={listing} /> : "N/A"}
      {listing?.prices.compare ? <Price className="discounted" value={listing.prices.compare} /> : null}
      {listing?.secondHand ? <span className="secondHand"></span> : null}
    </p>
  );
};

export function ArtistNames(props) {
  return (
    <p className="artists">
      {props.artists.map((a, index) => (
        <span key={index + a.name}>
          {a.id ? <Link to={`/catalogue?artists=${a.id}`}>{a.anv || a.name}</Link> : `${a.anv || a.name}`}
          {index < props.artists.length - 1 ? (a.join ? `${a.join !== "," ? " " : ""}${a.join} ` : ", ") : ""}
        </span>
      ))}
    </p>
  );
}

export { Tile, List, Full };
